import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { ApiInvite } from '#/api/invite/types/ApiInvite';
import { apiCreateTmpStorage } from '#/api/tmpStorage/apiCreateTmpStorage';
import { logIn } from '#/api/userEnvironment/apiUserEnvironment';
import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import type { FormUsageContext } from '#/components/Form/Form';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import type { LoginFormValues } from '#/components/LoginForm/LoginForm';
import { LoginForm } from '#/components/LoginForm/LoginForm';
import { useUserEnvironmentActions } from '#/contexts/UserEnvironmentContext';
import { useIsMounted } from '#/hooks/useIsMounted';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { AuthApiTmpStorage } from '#/modules/auth/api/tmpStorage/types/AuthApiTmpStorage';
import { useAuthProcessTmpStorage } from '#/modules/auth/hooks/useAuthProcessTmpStorage';
import type { AuthSignupPageLocationState } from '#/modules/auth/pages/signup/AuthSignup.page';
import { authMfaPath, authSignupPath } from '#/modules/auth/routing/auth.paths';
import { trackLogin } from '#/utils/analytics/track/trackLogin';
import { configureTrackJs } from '#/utils/configureTrackJs';
import { LOGIN_PROVIDER_NAMES } from '#/utils/constants/general/loginProviderNames';
import { createCookie, deleteCookie } from '#/utils/cookies';
import { getThirdPartyLoginUrl } from '#/utils/getThirdPartyLoginUrl';
import { HttpError } from '#/utils/HttpError';
import { generateRedirectUrl } from '#/utils/temporaryStorage';
import { hasParam } from '#/utils/url/hasParam';
import { tickFlashes } from '#/zustand-stores/flashesStore';
import { useHorizonFlashActions } from '#/zustand-stores/horizonFlashStore';
import { hideModal, showModal } from '#/zustand-stores/modalStore';

type Props = {
  invite?: ApiInvite | null;
  analyticsTitle: string;
  usageContext: FormUsageContext;
  redirectTo?: string | null;
  email?: string | null;
  tmpStorage?: AuthApiTmpStorage;
  inviteId?: string | null;
  onLogin?: (user: ApiUser) => void;
  signUpOverrideFn?: () => void;
  centered?: boolean;
  initialWarning?: string | null;
  compactView?: boolean;
  uncloseable?: boolean;
};

export function LoginFormContainer({
  invite,
  initialWarning: initialWarningFromProps,
  email,
  redirectTo,
  uncloseable,
  tmpStorage,
  analyticsTitle,
  usageContext,
  centered,
  compactView,
  inviteId,
  signUpOverrideFn,
  onLogin,
}: Props) {
  const { i18n, locale } = useI18n();

  const { setUserEnvironment } = useUserEnvironmentActions();

  const { clearHorizonFlash } = useHorizonFlashActions();

  const location = useLocation();
  const navigate = useNavigate();

  const isMounted = useIsMounted();

  const [thirdPartyLoginError, setThirdPartyLoginError] = useState<
    string | null | undefined
  >(null);
  const [initialWarning, setInitialWarning] = useState<
    string | null | undefined
  >(initialWarningFromProps);

  const initialValues = {
    email: email ?? '',
    password: '',
  };

  useEffect(() => {
    const { error, provider } = queryString.parse(location.search);
    if (!error) return;

    // @ts-expect-error TS(2538): Type '(string | null)[]' cannot be used as an inde... Remove this comment to see the full error message
    const providerName = LOGIN_PROVIDER_NAMES[provider as string] as string;
    let errorMessage;

    if (error === 'VM_VERIFY_EMAIL') {
      // Show VM email verification modal for OAuth login
      showModal('VM_EMAIL_VERIFICATION', {
        email: email || '',
      });

      return;
    }

    if (error === 'EXISTING_ACCOUNT' && providerName) {
      // TRANSLATORS: error message when user tries to log in using
      // TRANSLATORS: Facebook or Google but they already had an account
      // TRANSLATORS: that did not use that login provider
      errorMessage = i18n(
        'Please log in first to associate that %(provider)s account to your existing Idealist account',
        { provider: providerName },
      );
    }

    if (error === 'MISMATCHED_ACCOUNTS' && providerName) {
      // TRANSLATORS: User tried to link Facebook/Google account, but
      // TRANSLATORS: it was already used by another Idealist acccount
      errorMessage = i18n(
        'That %(provider)s account is already associated with another Idealist account',
        { provider: providerName },
      );
    }

    if (error === 'FAILED_OAUTH_LOGIN') {
      // TRANSLATORS: user used 3rd party authentication like Google, but it failed
      errorMessage = i18n(
        'Authentication did not work as expected, please try again.',
      );
    }

    if (error === 'USER_FROZEN') {
      // TRANSLATORS: user tried to login, but user is frozen
      errorMessage = i18n(
        'Your account has been frozen for violating our Terms of Service.',
      );
    }

    if (errorMessage) {
      // remove error and provider params from URL
      const params = queryString.parse(location.search);
      delete params.error;
      delete params.provider;
      void navigate(
        {
          pathname: location.pathname,
          search: queryString.stringify(params),
        },
        { replace: true },
      );
      setThirdPartyLoginError(errorMessage);
    }
  }, [i18n, location.search, location.pathname, navigate, email]);

  const showSignupModal = useCallback(
    (currentEmail: string) => {
      showModal('SIGNUP', {
        email: currentEmail,
        redirectTo,
        tmpStorage,
        uncloseable,
      });
    },
    [redirectTo, tmpStorage, uncloseable],
  );

  const getSignupUrl = useCallback(
    (currentEmail: string) => ({
      pathname: authSignupPath.with({ invite: inviteId?.toString() }),
      state: {
        email: currentEmail,
        redirectTo,
        tmpStorage,
      } satisfies AuthSignupPageLocationState,
    }),
    [inviteId, redirectTo, tmpStorage],
  );

  const showForgotPasswordModal = useCallback((state: { email: string }) => {
    showModal('FORGOT_PASSWORD', state);
  }, []);

  const getForgotPasswordUrl = useCallback(
    (state: { email: string }) => ({
      pathname: '/forgot-password',
      state,
    }),
    [],
  );

  const getThirdPartyLoginUrlCallback = useCallback(
    (provider: 'facebook' | 'google' | 'apple') =>
      getThirdPartyLoginUrl({
        locale,
        provider,
        redirectTo,
        inviteId,
        tmpStorage,
      }),
    [locale, redirectTo, inviteId, tmpStorage],
  );

  const { authProcessTmpStorage } = useAuthProcessTmpStorage();
  const submitLogin: FormSubmitFunction<LoginFormValues> = useCallback(
    async ({ values, handleError }) => {
      deleteCookie('userDismissedNotificationPreferencesDialog');
      setInitialWarning(null);

      let user: ApiUser | null = null;
      let response;
      try {
        response = await logIn(values);
      } catch (error) {
        // Check if this is a VM email verification error
        if (error instanceof HttpError && error.responseJson) {
          const vmVerifyEmailError = error.responseJson.errors?.find(
            (e) => e.type === 'vmVerifyEmail',
          );

          if (vmVerifyEmailError) {
            // Show VM email verification modal instead of form error
            showModal('VM_EMAIL_VERIFICATION', {
              email: values.email,
            });
            return;
          }
        }

        handleError(error as Error);
        return;
      } finally {
        if (isMounted()) {
          setThirdPartyLoginError(null);
        }
      }
      if (response.result === 'LOGIN_SUCCESS') {
        setUserEnvironment(response);
        ({ user } = response);
        trackLogin(user, 'Email Password');
        configureTrackJs(user.id);

        if (user.locale) {
          createCookie('_LOCALE_', user.locale, 1825);
        }

        hideModal();
        tickFlashes();
        clearHorizonFlash();

        if (tmpStorage) {
          const isSearchPage =
            hasParam(location.search, 'q') || hasParam(location.search, 'pq');

          const hasTemporaryStorage = Boolean(
            tmpStorage.type === 'redirect' &&
            tmpStorage.redirectUrl.includes('tmpId'),
          );

          await authProcessTmpStorage({
            tmpStorage,
            replaceOnNavigate: isSearchPage || hasTemporaryStorage,
          });
        }

        onLogin?.(user);
      } else {
        // response.result is 'MFA_REQUIRED';
        hideModal();
        const to = redirectTo ?? window.location.href;
        const tmpId = tmpStorage ? await apiCreateTmpStorage(tmpStorage) : null;

        await navigate(
          {
            pathname: authMfaPath.with({ token: response.mfaToken.id }),
            search: queryString.stringify({
              to: generateRedirectUrl(to, tmpId),
            }),
          },
          { replace: true },
        );
      }
    },
    [
      isMounted,
      setUserEnvironment,
      location.search,
      tmpStorage,
      onLogin,
      navigate,
      redirectTo,
      clearHorizonFlash,
      authProcessTmpStorage,
    ],
  );

  return (
    <LoginForm
      invite={invite}
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      initialValues={initialValues}
      thirdPartyLoginError={thirdPartyLoginError}
      showSignupModal={showSignupModal}
      getSignupUrl={getSignupUrl}
      signUpOverrideFn={signUpOverrideFn}
      showForgotPasswordModal={showForgotPasswordModal}
      getForgotPasswordUrl={getForgotPasswordUrl}
      getThirdPartyLoginUrl={getThirdPartyLoginUrlCallback}
      submitLogin={submitLogin}
      centered={centered}
      initialWarning={initialWarning}
      compactView={compactView}
    />
  );
}
